import { inject } from "@angular/core";
import { OrderService } from "./core/services/order.service";
import { ConfigService } from "./core/services/config.service";
import { catchError, combineLatest, concatMap, map, mergeMap, Observable, of, switchMap, take, tap } from "rxjs";
import { InitParamsService } from "./core/services/init-params.service";
import { MenuService } from "./core/services/menu.service";
import { Order } from "./core/models/ordering/order.model";
import {AuthService} from "./core/services/auth.service";
import { AccountCouponsService } from "./core/services/account/account-coupons.service";
import { TitleService } from "./core/services/title.service";
import { StoreStatusService } from "./core/services/store-status.service";
import { OrderType } from "./core/models/order-type.enum";
import { AppService } from "./app.service";
import { SmsModalServie } from "./core/services/sms-modal.service";
import { OrderTypeConfig } from "./core/models/order-type-config.model";
import { StoreConfig } from "./core/models/core/store-config.model";
import { AddressWrapperService } from "./core/services/address-wrapper.service";
import { Address } from "./core/models/core/address.model";
import { GTMService } from "./core/services/gtm.service";
import { Utils } from "./core/utils/utils";
import { FeaturedContentService } from "./core/services/featured-content.service";
import { ErrorsService } from "./core/services/errors.service";
import { ModalService } from "./core/services/modal.service";
import { LoyaltyService } from "./core/services/loyalty.service";
import { ParsedErrorResponse } from "./core/models/parsed-error-response.model";

/**
 * This function is executed after the app is bootstraped, and the AppComponent initializes
 * You can place here any initialization logic that is required to update the UI state
 * However, all other logic/state that is not required for the landing page, should be executed upon user action
 */

export const appInit = () => {
  // Inject/instantiate services and their state
  const initParams = inject(InitParamsService);
  const orderService = inject(OrderService);
  const configService = inject(ConfigService);
  const authService= inject(AuthService);
  const menuService = inject(MenuService)
  const accountCouponsService = inject(AccountCouponsService);
  const titleService = inject(TitleService);
  const storeStatusService = inject(StoreStatusService);
  const appService = inject(AppService);
  const smsModalService = inject(SmsModalServie);
  const addressWrapperService = inject(AddressWrapperService);
  const gtmService = inject(GTMService);
  const featuredContentService = inject(FeaturedContentService);
  const errorsService = inject(ErrorsService);
  const modalService = inject(ModalService);
  const loyaltyService = inject(LoyaltyService);

  function getOrderDerivedInitializers(order: Order, storeConfig: StoreConfig, orderTypes: OrderTypeConfig[]) {
    const obs: Array<Observable<any>> = [];

    if(!storeConfig){
      return obs;
    }

    const paramsOrderType = orderTypes.find(t => t.type === initParams.orderType);

    const orderType = (!!paramsOrderType && paramsOrderType.type) || undefined;
    const sourceIsMobile = initParams.sourceIsMobile;
    const sourceIsSelfOrder = initParams.sourceIsSelfOrder;
    const orderTable = initParams.tableNum?.trim() || undefined;

    const shouldCreateOrUpdateOrder = orderType || initParams.deferTime || sourceIsSelfOrder || orderTable;

    if(shouldCreateOrUpdateOrder || (sourceIsMobile && order.isInitialized)) {
      if(orderTable) initParams.markTableNumFromParams(orderTable);

      if (order.isInitialized) {
        if (order.orderType !== orderType || order.isMobileSource !== sourceIsMobile || order.isSelfOrderSource !== sourceIsSelfOrder || (!!orderTable && order.orderTable !== orderTable)) {
          const orderToUpdate = order.copy();
          if(orderType) orderToUpdate.orderType = orderType;
          if(orderTable) orderToUpdate.orderTable = orderTable;
          orderToUpdate.deferTime = initParams.deferTime;
          orderToUpdate.isMobileSource = sourceIsMobile;
          orderToUpdate.isSelfOrderSource = sourceIsSelfOrder;
          obs.push(orderService.updateOrder(orderToUpdate));
        }
      } else if(shouldCreateOrUpdateOrder) {
        const orderToCreate = order.copy();
        if(orderType) orderToCreate.orderType = orderType;
        if(orderTable) orderToCreate.orderTable = orderTable;
        orderToCreate.deferTime = initParams.deferTime;
        orderToCreate.isMobileSource = sourceIsMobile;
        orderToCreate.isSelfOrderSource = sourceIsSelfOrder;
        obs.push(
          orderService.createOrder(orderToCreate).pipe(
            catchError((error) => {
              const parsedError = errorsService.parseErrorResponse(error);
              const errorMessage = getCreateOrderErrorMessage(parsedError);

              modalService.openGenericErrorModal({
                modalMessage: errorMessage,
              });

              return of(null);
            })
          )
        );
      }
    }

    obs.push(featuredContentService.fetchFeaturedContents(orderType ?? order.orderType ?? OrderType.Any, initParams.deferTime ?? order.deferTime ?? '').pipe(take(1)));

    const orderTypeConfig = orderType ? configService.findOrderTypeConfig(orderType) : undefined;

    if ((!!orderTypeConfig && orderTypeConfig.needsAddress && initParams.address)) {
      obs.push(
        findAddress().pipe(
          mergeMap((a: Address) => orderService.updateOrderCustomerAddress(a)),
          map(res => !!res),
          catchError(() => of(false)),
          take(1)
        )
      );
    }

    return obs;
  }

  function getCreateOrderErrorMessage(parsedError: ParsedErrorResponse): string | undefined {
    const fieldErrors = parsedError.errors
      ? Object.entries(parsedError.errors)
          .flatMap(([field, errors]) => Array.isArray(errors) ? errors.map(error => `${field}: ${error}`) : [`${field}: ${errors}`])
          .filter(error => !!error)
      : [];

    return fieldErrors.length
      ? fieldErrors.join('<br />')
      : parsedError.allErrors.find((err) => typeof err === 'string' && !!err) ?? parsedError.message;
  }

  function findAddress(): Observable<Address> {
    return addressWrapperService.extractAddressAttributesFromParams().pipe(map(r => Object.assign(new Address(), r)));
  }

  function majorFetchFunctionWrapper<R>(observable: Observable<R> | Observable<R>[]): Observable<R | R[] | null> {
    if (Array.isArray(observable)) {
      return combineLatest(observable).pipe(
        catchError(() => {
          return of(null);
        }),
        take(1)
      );
    }
    return observable.pipe(
      catchError(() => {
        return of(null);
      }),
      take(1)
    );
  }

  // Define API request streams that need custom (error) handling
  const order$ = orderService.fetchOrder().pipe(
    catchError(err => {
      // This might be an error response but should not break the stream
      // as it just communicates that there's no order session yet
      if (err.error.message === 'Could not find order') {
        return of(null);
      }
      throw(new Error('Error fetching order'));
    }),
  );

  order$.pipe(
      concatMap(order =>
        // Step 1: fetchStoreConfig completes first
        configService.fetchStoreConfig().pipe(
          tap(() => {
            titleService.updateTitle();
            gtmService.initGTM();
          }),
          // Step 2: after store config is ready, run all other requests concurrently
          switchMap((storeConfig) =>
            combineLatest([
              of(order), // original order
              of(storeConfig), // store config result
              storeStatusService.fetchStoreStatus(
                order?.orderType ? order.orderType : OrderType.Any,
                true,
              ),
              configService.fetchSocialLinks(),
              configService.fetchOrderTypesConfig(initParams.sourceIsSelfOrder || order?.isSelfOrderSource ? 'SelfOrdering' : undefined),
              configService.fetchAddressTypesConfig(),
              configService.fetchLoyaltyPlanConfig(),
              menuService.fetchPanels(
                initParams.orderType ?? order?.orderType ?? OrderType.Any,
                initParams.deferTime ?? order?.deferTime ?? '',
                initParams.sourceIsSelfOrder || order?.isSelfOrderSource ? 'SelfOrdering' : 'Web',
              ),
              configService.fetchCreditCardConfig(),
              // authentication chain – starts only after fetchStoreConfig completes
              authService.fetchAccount().pipe(
                tap((account) => {
                  if (account) {
                    accountCouponsService.reloadCoupons();
                  }
                  if (initParams.foodTecTracker !== null) {
                    orderService.setSessionTrackerKey(initParams.foodTecTracker, window.location.href).subscribe();
                  }
                  smsModalService.initSmsModal();
                }),
                switchMap((account) => {
                  if (!Utils.isEmpty(account)) {
                    return of(account);
                  }
                  if (!Utils.isEmpty(initParams.accessToken)) {
                    return authService
                      .oAuthLogin(initParams.accessToken!)
                      .pipe(catchError(() => of(account)));
                  } else if (
                    !Utils.isEmpty(initParams.email) &&
                    !Utils.isEmpty(initParams.token)
                  ) {
                    return authService
                      .authTokenLogin(initParams.email!, initParams.token!)
                      .pipe(catchError(() => of(account)));
                  }
                  return of(account);
                }),
              ),
            ]),
          ),
          switchMap((results) =>
            loyaltyService.loadMembershipSubscription().pipe(
              catchError(() => of(null)),
              map(() => results)
            )
          ),
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          mergeMap(([order, storeConfig, storeStatus, socialLinks, orderTypes]) => {
            // Handle UTM params and set cookies
            initParams.getUtmParams();
            return majorFetchFunctionWrapper(getOrderDerivedInitializers(order!, storeConfig, orderTypes))
        })
        ),
    ),
    take(1),
  ).subscribe({
      complete: () => {
        //clearing params from the getOrderDerivedInitializers and UTM params
        initParams.clearParam([
          'orderType',
          'deferTime',
          'deferChoice',
          'addressName',
          'address',
          'addressType',
          'deliveryInstructions',
          'accessToken',
          'email',
          'token',
          'zip',
          'diff1',
          'diff2',
          'diff3',
          'lat',
          'lng',
          'utm_source',
          'utm_campaign',
          'tableNum',
          'store'
        ]);
      appService.appInitStatus.set('COMPLETE')
      },
      error: () => {
      appService.appInitStatus.set('ERROR')

    }
    });
}
